/**
 * F2b — Payment requirements FE wrapper.
 *
 * BE route-ok:
 *   GET    /groups/:id/requirements?status=&includeTargets=
 *   POST   /groups/:id/requirements
 *   GET    /requirements/:id?includeTargets=
 *   PATCH  /requirements/:id
 *   POST   /requirements/:id/publish
 *   POST   /requirements/:id/cancel       (body: { cancelReason })
 *   POST   /requirements/:id/close
 *   DELETE /requirements/:id              (csak draft)
 *   PUT    /requirements/:id/targets      (body: target upsert)
 *   DELETE /targets/:id
 *   GET    /my/requirements               (szülői view)
 *   GET    /groups/:id/currency-config    (primary + secondary currency scope-chain-nel)
 */

import type {
  PaymentRequirement,
  PaymentRequirementCancelInput,
  PaymentRequirementCreateInput,
  PaymentRequirementStatus,
  PaymentRequirementTarget,
  PaymentRequirementTargetUpsertInput,
  PaymentRequirementUpdateInput,
  PaymentRequirementWithTargets,
  SimilarRequirementHit,
} from '@pta/types';
import { apiCall } from './client';

// =====================================================================
// Requirement lista + részletek
// =====================================================================

export interface RequirementsListParams {
  status?: PaymentRequirementStatus;
  includeTargets?: boolean;
}

export function fetchGroupRequirements(
  groupId: number,
  params: RequirementsListParams & { includeTargets: true },
): Promise<PaymentRequirementWithTargets[]>;
export function fetchGroupRequirements(
  groupId: number,
  params?: RequirementsListParams,
): Promise<PaymentRequirement[]>;
export function fetchGroupRequirements(
  groupId: number,
  params?: RequirementsListParams,
): Promise<PaymentRequirement[] | PaymentRequirementWithTargets[]> {
  return apiCall(`/groups/${groupId}/requirements`, {
    query: {
      status: params?.status,
      includeTargets: params?.includeTargets === true ? 'true' : undefined,
    },
  });
}

export function fetchRequirement(
  id: number,
  opts: { includeTargets: true },
): Promise<PaymentRequirementWithTargets>;
export function fetchRequirement(
  id: number,
  opts?: { includeTargets?: false },
): Promise<PaymentRequirement>;
export function fetchRequirement(
  id: number,
  opts?: { includeTargets?: boolean },
): Promise<PaymentRequirement | PaymentRequirementWithTargets> {
  return apiCall(`/requirements/${id}`, {
    query: {
      includeTargets: opts?.includeTargets === true ? 'true' : undefined,
    },
  });
}

// =====================================================================
// Create / update / state transitions
// =====================================================================

export interface CreateRequirementResult {
  requirement: PaymentRequirement;
  targets: PaymentRequirementTarget[];
}

export function createRequirement(
  groupId: number,
  input: Omit<PaymentRequirementCreateInput, 'groupId'>,
): Promise<CreateRequirementResult> {
  return apiCall(`/groups/${groupId}/requirements`, {
    method: 'POST',
    body: input,
  });
}

export function updateRequirement(
  id: number,
  input: PaymentRequirementUpdateInput,
): Promise<PaymentRequirement> {
  return apiCall(`/requirements/${id}`, { method: 'PATCH', body: input });
}

export function publishRequirement(id: number): Promise<PaymentRequirement> {
  return apiCall(`/requirements/${id}/publish`, { method: 'POST' });
}

export function cancelRequirement(
  id: number,
  input: PaymentRequirementCancelInput,
): Promise<PaymentRequirement> {
  return apiCall(`/requirements/${id}/cancel`, {
    method: 'POST',
    body: input,
  });
}

export function closeRequirement(id: number): Promise<PaymentRequirement> {
  return apiCall(`/requirements/${id}/close`, { method: 'POST' });
}

export function deleteRequirement(id: number): Promise<PaymentRequirement> {
  return apiCall(`/requirements/${id}`, { method: 'DELETE' });
}

// =====================================================================
// Targets
// =====================================================================

export function upsertRequirementTarget(
  requirementId: number,
  input: PaymentRequirementTargetUpsertInput,
): Promise<PaymentRequirementTarget> {
  return apiCall(`/requirements/${requirementId}/targets`, {
    method: 'PUT',
    body: input,
  });
}

export function removeRequirementTarget(
  targetId: number,
): Promise<PaymentRequirementTarget> {
  return apiCall(`/targets/${targetId}`, { method: 'DELETE' });
}

// =====================================================================
// Parent view + group currency config
// =====================================================================

export interface ParentRequirementEntry {
  requirement: PaymentRequirement;
  targets: PaymentRequirementTarget[];
  students: Array<{ id: number; firstName: string; lastName: string }>;
  groupId: number;
  groupName: string;
  institutionId: number;
  institutionName: string;
}

export function fetchMyRequirements(): Promise<ParentRequirementEntry[]> {
  return apiCall('/my/requirements');
}

export interface GroupCurrencyConfig {
  primaryCurrency: string;
  secondaryCurrency: string | null;
  secondaryCurrencySource: 'global' | 'country' | 'institution' | 'group' | null;
}

export function fetchGroupCurrencyConfig(
  groupId: number,
): Promise<GroupCurrencyConfig> {
  return apiCall(`/groups/${groupId}/currency-config`);
}

export const REQUIREMENT_STATUSES: readonly PaymentRequirementStatus[] = [
  'draft',
  'published',
  'cancelled',
  'closed',
] as const;

// =====================================================================
// F2c — Hasonlósági javaslat (pg_trgm)
// =====================================================================

export function fetchSimilarRequirements(
  groupId: number,
  purpose: string,
  limit?: number,
): Promise<SimilarRequirementHit[]> {
  return apiCall(`/groups/${groupId}/requirements/similar`, {
    query: {
      purpose,
      limit: limit !== undefined ? String(limit) : undefined,
    },
  });
}
